import { useMemo } from 'react'
import { generateVillagers } from '@/economy/villagers'
import { ITEM_DISPLAY_NAMES, SPECIALTY_DISPLAY } from '@/economy/types'
import { useEconomyStore } from '@/state/useEconomyStore'
import type { WalletInfo } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'

interface PanelProps { interaction: InteractionTarget; wallet: WalletInfo | null }

export function HousePanel({ interaction }: PanelProps) {
  const villageData = useEconomyStore((s) => s.villageData.get(interaction.villageIndex))
  const marketPrices = villageData?.marketPrices ?? null

  const villagers = useMemo(() => {
    const profile = interaction.profileType
    if (!profile) return []
    const seed = 12345 + interaction.villageIndex * 777
    const sizes: Array<'small' | 'medium' | 'large'> = ['small', 'medium', 'large']
    const size = sizes[interaction.villageIndex % 3]
    const houseCounts = { small: 8, medium: 14, large: 22 }
    return generateVillagers(seed, size, profile, houseCounts[size], marketPrices)
  }, [interaction.villageIndex, interaction.profileType, marketPrices])

  // Find the villager assigned to this house
  const villager = villagers.find((v) => v.assignedHouseIndex === interaction.structureIndex)

  if (!villager) {
    return <p className="panel-placeholder">This house appears to be empty.</p>
  }

  const stateLabels: Record<string, string> = {
    gathering: 'Out gathering resources',
    idle: 'Resting at home',
    trading: 'At the trading post',
    sleeping: 'Sleeping',
  }

  const inventoryEntries = Object.entries(villager.inventory).filter(([, qty]) => (qty as number) > 0)

  return (
    <>
      <div className="panel-section">
        <h3 className="panel-section__title">{villager.name}</h3>
        <div className="panel-row">
          <span className="panel-row__label">Specialty</span>
          <span className="panel-row__value">{SPECIALTY_DISPLAY[villager.specialty]}</span>
        </div>
        <div className="panel-row">
          <span className="panel-row__label">Status</span>
          <span className="panel-row__value" style={{ color: villager.state === 'gathering' ? '#6b8b4a' : '#8b7355' }}>
            {stateLabels[villager.state] || villager.state}
          </span>
        </div>
        <div className="panel-row">
          <span className="panel-row__label">Gather Rate</span>
          <span className="panel-row__value">{villager.gatherRate}/min</span>
        </div>
      </div>

      <div className="panel-section">
        <h3 className="panel-section__title">Personal Stockpile</h3>
        {inventoryEntries.length === 0 && <p className="panel-placeholder">No items gathered yet.</p>}
        {inventoryEntries.map(([item, qty]) => (
          <div key={item} className="panel-row">
            <span className="panel-row__label">{ITEM_DISPLAY_NAMES[item as keyof typeof ITEM_DISPLAY_NAMES]} ×{qty}</span>
            <span className="panel-row__value" style={{ fontSize: 11, color: '#8b7355' }}>
              (1.5× market)
            </span>
          </div>
        ))}
      </div>
    </>
  )
}
